<template>
    <div v-if="can">
        <settings-heading
            :title="$t('settings.locations.title')"
            :description="$t('settings.locations.description')"
            :button="locations.length ? $t('settings.add_new', {subject: $t('settings.locations.location')}) : $t('settings.add_first', {subject: $t('settings.locations.location')})"
            button-icon="add-circle"
            @button-click="create"
        />

        <div class="mb-48">

            <loading v-if="fetchingLocations" class="pt-16"></loading>

            <div v-else-if="locations.length">

                <draggable
                    v-model="locations"
                    handle=".draggable"
                    item-key="id"
                    class="mb-32"
                >
                    <template #item="{ element }">
                        <div
                            :class="element.is_hq ? 'border-secondary border-b-4' : ''"
                            class="flex items-center h-96 rounded-6 overflow-hidden shadow-sm bg-white mb-12"
                        >
                            <img :src="element.thumbnail_url" class="h-full flex-none">

                            <div class="flex flex-col pl-16 mr-auto">
                                <span v-if="element.alias[locale]" class="first-letter:uppercase font-semibold">{{ element.alias[locale] }}</span>
                                <span>{{ element.street }}</span>
                                <span>{{ element.postal_code }} {{ element.city }}</span>
                            </div>

                            <div class="pl-16 h-full flex items-center">
                                <round-button icon="edit" class="mr-8" @click="edit(element)"></round-button>

                                <round-button icon="delete" @click="confirmDelete(element)"></round-button>

                                <div class="h-full pt-12 pl-16 flex flex-col">
                                    <div class="draggable rounded-6 text-primary-80 bg-primary-5 w-40 h-full flex items-center justify-center cursor-move mb-12 mr-12">
                                        <svg-icon type="drag-handle" class="max-h-16 max-w-16"></svg-icon>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </draggable>

                <div v-if="locations.length > 3" class="flex justify-end mb-48">
                    <simple-button
                        display="secondary"
                        icon="add-circle"
                        look="primary"
                        @click="create"
                    >
                        {{ $t('settings.add_new', {subject: locations.length ? $t('settings.locations.location') : $t('settings.locations.location')}) }}
                    </simple-button>
                </div>

            </div>

            <simple-button
                display="secondary"
                icon="add-circle"
                look="primary"
                v-else
                @click="create"
            >
                {{ $t('settings.add_first', {subject: $t('settings.locations.location')}) }}
            </simple-button>

        </div>

        <location-finder v-if="editing" :location="editing" @close="stopEdit()"></location-finder>
        <location-finder v-if="creating" @close="stopCreate()"></location-finder>

        <popup-modal-confirm
            :confirmText="$t('general.delete_subject', {subject: deleting.first_name})"
            :message="$t('general.confirm_delete_subject', {subject: deleting.first_name})"
            show-exclamation
            v-if="deleting"
            :working="performingDeleteRequest"
            @confirm="deleteLocation(deleting.id)"
            @close="cancelDelete"
        >
        </popup-modal-confirm>

    </div>
    <error-403 v-else></error-403>
</template>

<script>
import Loading from '../../components/ui/Loading.vue';
import * as locations from '../../services/LocationService';
import Location from '../../models/Location';
import { normalizeJson } from '../../../shared/helpers';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import LocationFinder from '../../components/ui/LocationFinder.vue';
import { mapState, mapActions } from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import RoundButton from '../../components/ui/RoundButton.vue';
import SvgIcon from '../../components/ui/SvgIcon.vue';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import draggable from 'vuedraggable';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import Error403 from '../../components/ui/Error403.vue';
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }


export default {
    components: {
        SettingsHeading,
        'loading': Loading,
        'simple-button': SimpleButton,
        'location-finder': LocationFinder,
        'round-button': RoundButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'draggable': draggable,
        'error-403': Error403,
    },
    data: function () {
        return {
            fetchingLocations: true,
            editing: null,
            creating: false,
            deleting: null,
            performingDeleteRequest: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        locations: {
            get () {
                return useRepo(Location).orderBy('order').get();
            },
            set (locations) {
                this.resort(locations);
            }
        },
        can () {
            return this.$can('settings.applications');
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        create () {
            this.creating = true;
        },
        stopCreate () {
            this.creating = false;
        },
        edit (location) {
            this.editing = location;
        },
        stopEdit () {
            this.editing = null;
        },
        confirmDelete (location) {
            this.deleting = location;
        },
        cancelDelete () {
            this.deleting = null;
        },
        deleteLocation (locationId) {
            this.performingDeleteRequest = true;

            locations.destroy(locationId).then(() => {
                useRepo(Location).destroy(locationId);
            }).finally(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            });
        },
        resort (sortedLocations) {
            let sort = sortedLocations.map((location, index) => ({ id: location.id, order: index }));

            let original = useRepo(Location).whereIn('id', sort.map(sort => sort.id)).get().map(locations => ({
                id: locations.id,
                order: locations.order
            }));

            useRepo(Location).save(sort);

            return locations.updateSort(sort)
                .then(result => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('general.order_changed', {subject: this.$t('settings.locations.title')}),
                    });
                })
                .catch(error => {
                    useRepo(Location).save(original);
                });
        }
    },
    mounted () {
        this.fetchingLocations = true;

        locations.index().then(result => {
            return useRepo(Location).save(normalizeJson(result.data));
        }).then(inserts => {
            this.fetchingLocations = false;
        });
    }
}
</script>
